import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, InputGroup, Alert } from 'react-bootstrap';
import { domainService } from '../../services/api';
import './Domains.css';

const DomainList = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [formData, setFormData] = useState({ url: '' });
  const [error, setError] = useState('');

  // Função para limpar a URL
  const cleanUrl = (url) => {
    try {
      // Remove espaços em branco
      let cleanedUrl = url.trim();
      
      // Remove protocolo (http:// ou https://)
      cleanedUrl = cleanedUrl.replace(/^(https?:\/\/)?(www\.)?/i, '');
      
      // Remove tudo após a primeira barra
      cleanedUrl = cleanedUrl.split('/')[0];
      
      return cleanedUrl;
    } catch (error) {
      return url;
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await domainService.getAll();
      
      if (!response || !response.data) {
        throw new Error('Resposta inválida da API');
      }
      
      let domainsArray = [];
      if (Array.isArray(response.data)) {
        domainsArray = response.data;
      } else if (response.data.domains && Array.isArray(response.data.domains)) {
        domainsArray = response.data.domains;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        domainsArray = response.data.data;
      }
      
      setDomains(domainsArray);
    } catch (error) {
      console.error('Erro ao carregar domínios:', error);
      setError('Falha ao carregar domínios. Por favor, tente novamente.');
      setDomains([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar domínios com base na pesquisa
  const filteredDomains = Array.isArray(domains) 
    ? domains.filter(domain => cleanUrl(domain?.url || '').toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  // Abrir modal para adicionar novo domínio
  const handleAddDomain = () => {
    setEditingDomain(null);
    setFormData({ url: '' });
    setShowModal(true);
  };

  // Abrir modal para editar domínio existente
  const handleEditDomain = (domain) => {
    setEditingDomain(domain);
    setFormData({ url: domain.url });
    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
  };

  // Atualizar dados do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'url') {
      setFormData({ ...formData, [name]: cleanUrl(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Salvar domínio (criar ou atualizar)
  const handleSaveDomain = async () => {
    if (!formData.url.trim()) {
      setError('O URL do domínio é obrigatório');
      return;
    }

    try {
      const cleanedFormData = {
        ...formData,
        url: cleanUrl(formData.url)
      };

      if (editingDomain) {
        // Atualizar domínio existente
        await domainService.update(editingDomain._id, cleanedFormData);
      } else {
        // Criar novo domínio
        await domainService.create(cleanedFormData);
      }
      
      // Recarregar lista de domínios
      fetchDomains();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar domínio:', error);
      setError('Falha ao salvar domínio. Por favor, tente novamente.');
    }
  };

  // Excluir domínio
  const handleDeleteDomain = async (domainId) => {
    if (window.confirm('Tem certeza que deseja excluir este domínio?')) {
      try {
        await domainService.delete(domainId);
        fetchDomains();
      } catch (error) {
        console.error('Erro ao excluir domínio:', error);
        alert('Falha ao excluir domínio. Por favor, tente novamente.');
      }
    }
  };

  return (
    <Container fluid className="domains-container">
      <h1 className="mb-4">Domínios</h1>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              placeholder="Pesquisar domínios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="text-end">
          <Button variant="primary" onClick={handleAddDomain}>
            Adicionar Domínio
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : filteredDomains.length === 0 ? (
            <div className="text-center p-5">
              {searchTerm ? 'Nenhum domínio encontrado para a pesquisa' : 'Nenhum domínio cadastrado'}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>URL</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredDomains.map(domain => (
                  <tr key={domain._id}>
                    <td>{domain._id}</td>
                    <td>{cleanUrl(domain.url)}</td>
                    <td>{domain.createdAt ? new Date(domain.createdAt).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleEditDomain(domain)}
                      >
                        Editar
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteDomain(domain._id)}
                      >
                        Excluir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para adicionar/editar domínio */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingDomain ? 'Editar Domínio' : 'Adicionar Domínio'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger">{error}</div>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>URL do Domínio</Form.Label>
              <Form.Control
                type="text"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="Ex: exemplo.com"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveDomain}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DomainList; 